import { useEffect, useRef, useState } from 'react';
import { Clock, QrCode, Smartphone } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import { Cargando } from '../ui/EstadoDePantalla';
import { MODULO_PASAR_LISTA, contextoDeTerminal } from './enlaceDeTerminal';

/* =====================================================================
   Pasar lista con el teléfono · DISEÑO-ASISTENCIA §8 (contrato 2.8.0)
   =====================================================================
   Se llega escaneando el QR de la terminal (`/T/{código}/{K}`) o tecleando
   el número de 4 dígitos que muestra. El servidor canjea la K por un pase
   de 2 minutos, dice «Hola, …», los eventos válidos y el sugerido; aquí el
   sugerido es el botón primario a todo lo ancho y los demás van debajo.
   ===================================================================== */
const NOMBRES = {
  ENTRADA: 'Entrada', INICIO_COMIDA: 'Salir a comer', FIN_COMIDA: 'Regresar de comer',
  SALIDA_CAMPO: 'Salir a campo', REGRESO_CAMPO: 'Regresar de campo', SALIDA: 'Salida',
};
const COLOR = {
  VERDE: 'border-green-300 bg-green-50 text-green-900',
  AMBAR: 'border-amber-300 bg-amber-50 text-amber-900',
  AZUL: 'border-sky-300 bg-sky-50 text-sky-900',
  ROJO: 'border-red-300 bg-red-50 text-red-900',
};
const uuid = () => (globalThis.crypto?.randomUUID
  ? globalThis.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));
const limpiarCodigo = (t) => (t || '').toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 6);
const limpiarNumero = (t) => (t || '').replace(/\D/g, '').slice(0, 4);

function Aviso({ error, onOtraVez }) {
  return (
    <div role="alert" className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
      <div className="font-semibold">{error.mensaje}</div>
      {error.queHacer && <p className="text-sm">{error.queHacer}</p>}
      {onOtraVez && (
        <button type="button" onClick={onOtraVez}
          className="min-h-12 w-full rounded-lg border border-amber-400 bg-white px-4 font-semibold">
          Volver a escanear
        </button>
      )}
    </div>
  );
}

export default function PasarLista({ contexto: dado = {} }) {
  // El contexto llega de la dirección (`?m=pasar-lista&codigo=&k=`) o de quien monta.
  const contexto = { ...(contextoDeTerminal(MODULO_PASAR_LISTA, typeof window !== 'undefined' ? window.location.search : '') || {}), ...dado };
  const [codigo, setCodigo] = useState(contexto.codigo || '');
  const [numero, setNumero] = useState('');
  const [lectura, setLectura] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [turno, setTurno] = useState(null);
  const via = useRef(null);
  const leido = useRef(false);

  async function leer(cod, q) {
    setCargando(true); setError(null); setResultado(null);
    via.current = { codigo: cod, ...q };
    try {
      const params = new URLSearchParams(q).toString();
      setLectura(await api.get(`/asistencia/terminal/${encodeURIComponent(cod)}?${params}`));
    } catch (e) {
      setLectura(null);
      setError({ mensaje: e.message, queHacer: e.queHacer, codigo: e.codigo });
    } finally { setCargando(false); }
  }

  useEffect(() => {
    if (leido.current) return;
    if (contexto.codigo && (contexto.k || contexto.numero)) {
      leido.current = true;
      leer(contexto.codigo, contexto.k ? { k: contexto.k } : { numero: contexto.numero });
    }
  }, [contexto.codigo, contexto.k, contexto.numero]);

  async function marcar(evento) {
    setCargando(true); setError(null);
    try {
      const r = await api.post('/asistencia/marcar', { pase: lectura.pase, evento, uuid_cliente: uuid() });
      setResultado(r);
      setLectura({ ...lectura, estado_dia: r.estado_dia, eventos_validos: r.eventos_validos,
        evento_sugerido: r.evento_sugerido });
    } catch (e) {
      setError({ mensaje: e.message, queHacer: e.queHacer, codigo: e.codigo });
    } finally { setCargando(false); }
  }

  async function usarTerminal() {
    setCargando(true); setError(null);
    try {
      const v = via.current;
      setTurno(await api.post('/terminal/v2/turno/abrir', {
        codigo_terminal: v.codigo, ...(v.k ? { k: v.k } : { numero: v.numero }) }));
    } catch (e) {
      setError({ mensaje: e.message, queHacer: e.queHacer, codigo: e.codigo });
    } finally { setCargando(false); }
  }

  const codigoListo = codigo.length === 6;
  const numeroListo = numero.length === 4;
  const motivo = !codigoListo ? 'Escribe el código de 6 letras y números de la terminal.'
    : !numeroListo ? 'Escribe los 4 dígitos que muestra la terminal.' : null;
  const sugerido = lectura?.evento_sugerido;
  const otros = (lectura?.eventos_validos || []).filter((e) => e !== sugerido);

  return (
    <div className="mx-auto w-full max-w-md space-y-4" data-terminal="pasar-lista">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-gray-900">Pasar lista</h1>
        <p className="text-sm text-gray-600">Registra tu entrada, tu comida y tu salida con el teléfono.</p>
      </header>
      <Ayuda id="terminal.pasarlista.pantalla" />

      {cargando && <Cargando filas={2} etiqueta="Un momento" />}
      {error && <Aviso error={error}
        onOtraVez={['QR_VENCIDO', 'PASE_VENCIDO'].includes(error.codigo) ? () => { setError(null); setLectura(null); } : null} />}

      {!lectura && !cargando && (
        <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <QrCode className="mt-0.5 h-6 w-6 shrink-0 text-gray-500" aria-hidden="true" />
            <p className="text-sm text-gray-700">Escanea el QR de la terminal o escribe el número que muestra.</p>
          </div>
          <label className="block text-sm font-medium text-gray-800">
            Código de la terminal
            <input value={codigo} onChange={(e) => setCodigo(limpiarCodigo(e.target.value))}
              autoCapitalize="characters" className="mt-1 min-h-12 w-full rounded-lg border px-3 text-lg tracking-widest"
              placeholder="M4R7WX" aria-describedby="ayuda-codigo" />
          </label>
          <label className="block text-sm font-medium text-gray-800">
            Número de 4 dígitos
            <input value={numero} onChange={(e) => setNumero(limpiarNumero(e.target.value))}
              inputMode="numeric" className="mt-1 min-h-12 w-full rounded-lg border px-3 text-2xl tracking-[0.5em]"
              placeholder="0000" />
          </label>
          <Ayuda id="terminal.pasarlista.numero" />
          <button type="button" disabled={!!motivo} onClick={() => leer(codigo, { numero })}
            className="min-h-12 w-full rounded-lg bg-blue-600 px-4 font-semibold text-white disabled:bg-gray-300 disabled:text-gray-600">
            Continuar
          </button>
          {motivo && <p className="text-sm text-gray-600" data-motivo="continuar">{motivo}</p>}
        </div>
      )}

      {lectura && (
        <section className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-2xl font-bold text-gray-900">{lectura.saludo}</div>
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" aria-hidden="true" />
              {lectura.horario_hoy?.laboral
                ? <span>Hoy: {lectura.horario_hoy.entrada} a {lectura.horario_hoy.salida} · comida {lectura.horario_hoy.comida_min} min</span>
                : <span>Hoy no tienes horario; lo que registres queda anotado.</span>}
            </div>
            <div className="text-sm text-gray-500">Terminal: {lectura.terminal?.nombre}</div>
          </div>
          {resultado && (
            <div role="status" className={`rounded-xl border p-4 font-semibold ${COLOR[resultado.color] || COLOR.VERDE}`}>
              {resultado.texto}
            </div>
          )}
          {sugerido && (
            <button type="button" onClick={() => marcar(sugerido)} data-sugerido={sugerido}
              className="min-h-14 w-full rounded-xl bg-blue-600 px-4 text-lg font-bold text-white">
              {NOMBRES[sugerido] || sugerido}
            </button>
          )}
          {otros.map((e) => (
            <button key={e} type="button" onClick={() => marcar(e)}
              className="min-h-12 w-full rounded-xl border border-gray-300 bg-white px-4 font-semibold text-gray-800">
              {NOMBRES[e] || e}
            </button>
          ))}
          {lectura.puede_usar_terminal && !turno && (
            <button type="button" onClick={usarTerminal}
              className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 font-semibold text-gray-800">
              <Smartphone className="h-5 w-5" aria-hidden="true" /> Usar la terminal
            </button>
          )}
          {lectura.puede_usar_terminal && <Ayuda id="terminal.pasarlista.usar_terminal" />}
          {turno && <div role="status" className={`rounded-xl border p-4 ${COLOR.VERDE}`}>{turno.mensaje}</div>}
        </section>
      )}
    </div>
  );
}
